/**
 * Keeping a media element in step with the sequence clock.
 *
 * Starting or seeking a media element is not instant: Safari/WebKit needs
 * about 0.25-0.5 s before new samples are really presented (Chrome far less),
 * and its 'playing'/'seeked' events fire before that, so they cannot be used to
 * measure it. The element plays on from where it was told to, so it ends up
 * that much behind the wall clock. A fixed drift limit (0.25 s) smaller than
 * that latency re-seeks on every check, and every seek is again late: playback
 * stutters at the seek rhythm, picture and sound. The cure: after a start or
 * seek, give the element time to settle; if it is then still off by more than
 * the limit, correct once and learn the offset (the "lead") by which to aim
 * ahead from then on. Real drift is still corrected, only without a loop.
 *
 * The same latency shows at the start of playback and at cuts. Play therefore
 * starts the elements and starts the clock only when the main picture has
 * really presented a frame (the clock then begins at that frame's media time,
 * and the measured delay becomes the lead). Upcoming clips are pre-rolled:
 * started early, hidden and silent, so at the cut they already run. A split
 * that removed nothing keeps one element instead of cutting to a second.
 */

/** Seek only for real drift (seconds). */
export const DRIFT_LIMIT = 0.25;
/** Largest lead the element is aimed ahead by (seconds). */
export const MAX_LEAD = 1;
/** After a start or seek, how long the element is left alone before drift is judged (ms). */
export const SETTLE_MS = 800;
/** Paused elements are put exactly on the frame when they are off by more than this (seconds). */
export const PAUSED_TOLERANCE = 0.02;
/** Pre-roll is the learned start lead plus this margin (seconds). */
export const PREROLL_MARGIN = 0.7;
/** Longest pre-roll (seconds). */
export const MAX_PREROLL = MAX_LEAD + PREROLL_MARGIN;
/**
 * Volume of a pre-rolled clip before its cut. Not 0: browsers (Safari) pause an element that was playing
 * silently when it becomes audible without a user gesture; -60 dB is inaudible and keeps it "audible" to them.
 * Since 0.96.0 the sequence viewer routes its elements through Web Audio: the element's own volume stays at 1 (so it
 * is always "audible" to the browser) and this level is applied by the element's gain node instead (see routeVolume).
 */
export const PREROLL_VOLUME = 0.001;
/** Highest volume (gain) a clip can ask for: +12 dB, the clip gain's maximum. Above 1 only a gain node can play it. */
export const MAX_GAIN = Math.pow(10, 12 / 20);
/** Longest wait for the first frame at Play before the clock starts anyway (ms). */
export const FIRST_FRAME_TIMEOUT_MS = 1000;

export interface ClockState {
  /** Learned start/seek latency: how far ahead of the clock to aim (seconds). */
  lead: number;
  /** No drift judgement before this time (ms). */
  settleUntil: number;
}

/** Learned from any element; new elements start from it. */
let sharedLead = 0;

export const createClockState = (): ClockState => ({ lead: sharedLead, settleUntil: 0 });
/** The lead new elements start from. */
export const currentLead = (): number => sharedLead;
/** The start delay seen at Play (play() to first presented frame) becomes the lead. */
export function noteStartLatency(seconds: number): void {
  sharedLead = Math.max(0, Math.min(MAX_LEAD, seconds));
}

export type MediaAction = { kind: 'none' } | { kind: 'start'; to: number } | { kind: 'seek'; to: number; lead: number };

/** What a playing element needs now: start, seek (real drift only, once settled), or nothing. Pure. */
export function planPlaying(i: { paused: boolean; seeking: boolean; currentTime: number; target: number; now: number; st: ClockState }): MediaAction {
  if (i.paused) return { kind: 'start', to: Math.max(0, i.target + i.st.lead) };
  if (i.seeking || i.now < i.st.settleUntil) return { kind: 'none' };
  const behind = i.target - i.currentTime;
  if (Math.abs(behind) <= DRIFT_LIMIT) return { kind: 'none' };
  // Still off after settling: whatever lead was aimed for was wrong by this much.
  const lead = Math.max(0, Math.min(MAX_LEAD, i.st.lead + behind));
  return { kind: 'seek', to: Math.max(0, i.target + lead), lead };
}

/** A paused element sits exactly on the requested time (frame stepping, scrubbing). Pure. */
export const planPaused = (currentTime: number, target: number): number | null => (Math.abs(currentTime - target) > PAUSED_TOLERANCE ? target : null);

/** Apply a planned start/seek to the clock state. */
export function noteCommand(st: ClockState, a: MediaAction, now: number): void {
  if (a.kind === 'none') return;
  st.settleUntil = now + SETTLE_MS;
  if (a.kind === 'seek') { st.lead = a.lead; sharedLead = a.lead; }
}

const states = new WeakMap<HTMLMediaElement, ClockState>();
const written = new WeakMap<HTMLElement, Record<string, string>>();
const volumes = new WeakMap<HTMLMediaElement, number>();
/** Elements whose volume is a Web Audio gain (0.96.0): setVolume hands the value to it instead of el.volume. */
const routes = new WeakMap<HTMLMediaElement, (gain: number) => void>();

/**
 * Route an element's volume to `apply` (its gain node), or back to el.volume (null). The last written value is
 * forgotten, so the next setVolume reaches the new target even when the value is the same.
 */
export function routeVolume(el: HTMLMediaElement, apply: ((gain: number) => void) | null): void {
  if (apply) routes.set(el, apply); else routes.delete(el);
  volumes.delete(el);
}

/** Write style properties, skipping those whose value is already applied. */
export function setStyles(el: HTMLElement, props: Record<string, string>): void {
  let last = written.get(el);
  if (!last) written.set(el, (last = {}));
  const style = el.style as unknown as Record<string, string>;
  for (const k in props) {
    const v = props[k]!;
    if (last[k] === v) continue;
    last[k] = v;
    style[k] = v;
  }
}

/**
 * Volume, written only when it changed: to the element's gain node when it is routed through Web Audio (any
 * value up to MAX_GAIN, so clips above 0 dB are heard louder), otherwise to el.volume (at most 1).
 */
export function setVolume(el: HTMLMediaElement, volume: number): void {
  const v = Math.max(0, Math.min(MAX_GAIN, volume));
  if (volumes.get(el) === v) return;
  volumes.set(el, v);
  const route = routes.get(el);
  if (route) route(v);
  else el.volume = Math.min(1, v);
}

/** Bring an element that is part of the picture/sound to `target` seconds. */
export function followClock(el: HTMLMediaElement, target: number, isPlaying: boolean, now = performance.now()): void {
  let st = states.get(el);
  if (!st) states.set(el, (st = createClockState()));
  if (isPlaying) {
    const a = planPlaying({ paused: el.paused, seeking: el.seeking, currentTime: el.currentTime, target, now, st });
    if (a.kind === 'none') return;
    noteCommand(st, a, now);
    if (a.kind === 'seek' || Math.abs(el.currentTime - a.to) > PAUSED_TOLERANCE) el.currentTime = a.to;
    if (a.kind === 'start') void el.play().catch(() => undefined);
  } else {
    if (!el.paused) el.pause();
    const to = planPaused(el.currentTime, target);
    if (to !== null) el.currentTime = to;
  }
}

/** Start a paused element exactly at `target` (no lead: Play waits for the first frame instead). */
export function startHeld(el: HTMLMediaElement, target: number, now = performance.now()): void {
  if (!el.paused) return;
  let st = states.get(el);
  if (!st) states.set(el, (st = createClockState()));
  noteCommand(st, { kind: 'start', to: target }, now);
  if (Math.abs(el.currentTime - target) > PAUSED_TOLERANCE) el.currentTime = target;
  void el.play().catch(() => undefined);
}

/**
 * Sequence time to start the clock at, from the media time of the first
 * presented frame of the main element: the playhead matches what is shown.
 * Never before the requested start, at most two seconds after it. Pure.
 */
export function clockBaseFromFrame(i: { base: number; mainSeconds: number; mediaTime: number; ticksPerSecond: number }): number {
  const t = i.base + Math.round((i.mediaTime - i.mainSeconds) * i.ticksPerSecond);
  return Math.max(i.base, Math.min(i.base + 2 * i.ticksPerSecond, t));
}

/** How long before its cut an upcoming clip starts running (seconds): the lead plus a margin, bounded. */
export const prerollFor = (lead: number): number => Math.min(MAX_PREROLL, Math.max(PREROLL_MARGIN, lead + PREROLL_MARGIN));

export type UpcomingPlan = { kind: 'run'; target: number } | { kind: 'cue'; to: number };

/**
 * What an upcoming clip's element does. `sourceIn` is the clip's first source
 * second, `dt` the seconds until its cut. While playing it starts running
 * (hidden, silent) once `dt` is within the pre-roll, aimed at the position it
 * will have at the cut; with less material before sourceIn than the pre-roll
 * it starts later, from 0, so it never starts before the beginning of the
 * source. Paused, or before that moment, it is only cued. Pure.
 */
export function planUpcoming(i: { playing: boolean; sourceIn: number; dt: number; lead: number }): UpcomingPlan {
  if (!i.playing) return { kind: 'cue', to: i.sourceIn };
  const startDt = Math.min(prerollFor(i.lead), i.sourceIn + i.lead);
  if (i.dt <= startDt) return { kind: 'run', target: i.sourceIn - i.dt };
  return { kind: 'cue', to: Math.max(0, i.sourceIn - startDt + i.lead) };
}

/** A clip on one track, in ticks, for continuity detection. */
export interface TrackClip { id: string; versionId: string; recordIn: number; recordOut: number; sourceIn: number; sourceOut: number; transitionIn?: boolean }

/**
 * Clips that continue each other (same version, adjacent on the track, the
 * next one's source starting where the previous one's ends, within `tol`) share
 * one element: the id of the first clip of the chain. Others map to themselves.
 * `clips` is one track in time order. A clip with a transition into it is
 * never merged. Pure.
 */
export function continuityHeads(clips: TrackClip[], tol: number): Map<string, string> {
  const head = new Map<string, string>();
  let prev: TrackClip | undefined;
  for (const c of clips) {
    const joins = prev && !c.transitionIn && c.versionId === prev.versionId
      && Math.abs(c.recordIn - prev.recordOut) <= tol && Math.abs(c.sourceIn - prev.sourceOut) <= tol;
    head.set(c.id, joins ? head.get(prev!.id)! : c.id);
    prev = c;
  }
  return head;
}
